import React, { useEffect } from 'react';
import { LegacyColumn as Column } from 'components/MaterialTable/MatDataGridTypes';
import styled, { createGlobalStyle } from 'styled-components';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { GENERAL_CONSTANTS } from 'constants/general';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { BUSINESS_UNIT_USERS_COLUMNS } from 'constants/usersBusinessUnit';
import { Main } from 'styles/themeColors';
import TableHeader from 'components/UI/MultiTableHeader';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import MatDataGrid from 'components/MaterialTable/MatDataGrid';


const StyledButton = styled.button<any>`
  background: none;
  border: none;
  color: ${({ color }) => color || Main.brandDark};
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  margin: 0 5px;
  
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const UsersTableStyles = createGlobalStyle`
  .business-unit-users-table-wrapper {
    /* Target Material React Table checkbox column selectors */
    .mrt-row-select,
    .mrt-table-head-cell-select,
    .mrt-table-body-cell-select,
    tbody tr td:first-child,
    thead tr th:first-child {
      padding: 8px 12px !important;
      width: 2% !important;
      min-width: 2% !important;
      max-width: 2% !important;
      vertical-align: middle !important;
    }

    .mrt-row-select:has(input[type="checkbox"]),  
    .mrt-table-head-cell-select,  
    .mrt-table-body-cell-select,  
    table thead tr th:first-child, 
    table tbody tr td:first-child,  
    .MuiTableHead-root .MuiTableCell-head:first-child,  
    .MuiTableBody-root .MuiTableCell-body:first-child,  
    th[data-column-id="mrt-row-select"],  
    td[data-column-id="mrt-row-select"] {
      width: 2% !important;
      min-width: 2% !important;
      max-width: 2% !important;
      padding: 4px !important;
    }
    
    /* Target Material-UI checkbox cells */
    .MuiTableCell-paddingCheckbox {
      padding: 8px 12px !important;
    }
    
    /* Target the actual checkbox component */
    .MuiCheckbox-root {
      padding: 4px !important;
      margin: 0 !important;
    }
    
    /* Additional fallback selectors */
    table tbody tr td:first-child,
    table thead tr th:first-child {
      padding: 8px 12px !important;
    }
    
    /* Material React Table specific class targeting */
    [class*="MuiTableCell"]:first-child {
      padding: 8px 12px !important;
    }
    
    /* Business Unit column width and padding - target second column when checkboxes are enabled */
    table thead tr th:nth-child(2),
    table tbody tr td:nth-child(2) {
      width: 200px !important;
      min-width: 200px !important;
      padding: 16px 8px !important;
    }
    
    /* Ensure all other columns (non-checkbox, non-business unit) have consistent padding */
    table thead tr th:not(:first-child):not(:nth-child(2)),
    table tbody tr td:not(:first-child):not(:nth-child(2)) {
      padding: 16px 8px !important;
    }
  }
`;


// Pure presentation component interface
interface BusinessUnitUsersTableProps {
    // Data props - accepts any array with id field (BusinessUnitUser or AdminUser)
    data: Array<{ id: number; name: string; email: string; role: string;[key: string]: any }>;
    totalCount: number;
    loading: boolean;
    tableKey: string;
    hasInitialLoad: boolean;

    // Pagination props
    currentPage: number;
    rowsPerPage: number;
    onPageChange: (newPage: number) => void;
    onRowsPerPageChange: (newRowsPerPage: number) => void;

    // Sorting props
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSortChange: (field: string, direction: 'asc' | 'desc') => void;

    // Selection props
    selectedRows: any[];
    onSelectionChange: (rows: any[]) => void;

    // Action handlers
    onEdit: (userId: number) => void;
    onRemove: (userId: number) => void;
    onRemoveBulkUser: (selectedRows: any[]) => void;

    // UI state
    isVendorOnly: boolean;
    columns?: Column<any>[];
    actionsDisabled?: boolean;
}

const BusinessUnitUsersTable: React.FC<BusinessUnitUsersTableProps> = ({
    // Data props
    data,
    totalCount,
    loading,
    tableKey,
    hasInitialLoad,

    // Pagination props
    currentPage,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,

    // Sorting props
    onSortChange,

    // Selection props
    selectedRows,
    onSelectionChange,

    // Action handlers
    onEdit,
    onRemove,
    onRemoveBulkUser,
    // UI state
    isVendorOnly,
    columns: customColumns,
    actionsDisabled = false
}) => {
    const [tableColumns, setTableColumns] = React.useState<Column<any>[]>(
        customColumns || BUSINESS_UNIT_USERS_COLUMNS
    );

    const { readOnly: isEditUsersReadOnly } = useReadPermission(PermissionCode.ORGANIZATION_EDIT_USERS);
    const { readOnly: isRemoveUsersReadOnly } = useReadPermission(PermissionCode.ORGANIZATION_REMOVE_USERS);

    // Determine if actions should be disabled (either by permission or explicit prop)
    const isEditDisabled = isEditUsersReadOnly || actionsDisabled;
    const isRemoveDisabled = isRemoveUsersReadOnly || actionsDisabled;

    // Setup columns with action handlers
    useEffect(() => {
        const actionColumn = {
            render: (rowData: any) => {
                return (
                    <FlexboxContainerComponent>
                        {!isVendorOnly && <>
                            <ReadOnlyWrapper readOnly={isEditDisabled} tooltipMessage={actionsDisabled ? '' : undefined}>
                                <StyledButton
                                    onClick={isEditDisabled ? null : () => onEdit(rowData?.id)}
                                    disabled={loading || actionsDisabled}
                                    title="Edit user"
                                >
                                    {GENERAL_CONSTANTS.EDIT}
                                </StyledButton>
                            </ReadOnlyWrapper>

                            <ReadOnlyWrapper readOnly={isRemoveDisabled} tooltipMessage={actionsDisabled ? '' : undefined}>
                                <StyledButton
                                    onClick={isRemoveDisabled ? null : () => onRemove(rowData?.id)}
                                    disabled={loading || actionsDisabled}
                                    title="Remove user"
                                    color={Main.error2}
                                >
                                    {GENERAL_CONSTANTS.REMOVE}
                                </StyledButton>
                            </ReadOnlyWrapper>
                        </>}
                    </FlexboxContainerComponent>
                );
            },
        };

        const baseColumns = customColumns || BUSINESS_UNIT_USERS_COLUMNS;
        const updatedColumns = baseColumns.map((col) => {
            if (col.field === 'actions') {
                return { ...col, ...actionColumn };
            }
            return col;
        });

        setTableColumns(updatedColumns);
    }, [isVendorOnly, onEdit, onRemove, loading, customColumns, actionsDisabled, isEditDisabled, isRemoveDisabled]);

    // Handle Material Table sort change
    const handleMaterialTableSort = React.useCallback((orderBy: number, orderDirection: string) => {
        if (orderBy >= 0 && tableColumns && tableColumns[orderBy] && (orderDirection === 'asc' || orderDirection === 'desc')) {
            const field = String(tableColumns[orderBy].field);
            onSortChange(field, orderDirection);
        }
    }, [tableColumns, onSortChange]);

    // Show loading state during initial load
    if (loading && !hasInitialLoad) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <LoadingIndicator />
            </div>
        );
    }

    const totalPages = Math.ceil(totalCount / rowsPerPage);

    // Render table
    return (
        <div className="business-unit-users-table-wrapper">
            <UsersTableStyles />
            <TableHeader handleDelete={onRemoveBulkUser} selectedRows={selectedRows} readOnly={isRemoveDisabled} hideDeleteBtn={isVendorOnly} />
            <MatDataGrid
                key={tableKey}
                columns={tableColumns}
                data={data}
                isLoading={loading}
                sorting
                enableSelection={!isVendorOnly}
                onSelectionChange={onSelectionChange}
                onOrderChange={handleMaterialTableSort}
                headerStyle={{
                    fontSize: '16px',
                    height: 50,
                    fontWeight: 400
                }}
                rowStyle={{
                    fontSize: '14px',
                    wordWrap: 'break-word',
                }}
                customPagination={{
                    itemsPerPage: rowsPerPage,
                    setItemsPerPage: onRowsPerPageChange,
                    currentPage: currentPage + 1,
                    setCurrentPage: onPageChange,
                    totalPages: totalPages > 0 ? totalPages : 1,
                    baseRoute: 'business-unit-users',
                    shouldRedirect: false
                }}
            />
        </div>
    );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(BusinessUnitUsersTable, (prevProps, nextProps) => {
    // Custom comparison function for better performance
    return (
        prevProps.data === nextProps.data &&
        prevProps.totalCount === nextProps.totalCount &&
        prevProps.loading === nextProps.loading &&
        prevProps.tableKey === nextProps.tableKey &&
        prevProps.currentPage === nextProps.currentPage &&
        prevProps.rowsPerPage === nextProps.rowsPerPage &&
        prevProps.sortBy === nextProps.sortBy &&
        prevProps.sortOrder === nextProps.sortOrder &&
        prevProps.selectedRows.length === nextProps.selectedRows.length &&
        prevProps.isVendorOnly === nextProps.isVendorOnly &&
        prevProps.hasInitialLoad === nextProps.hasInitialLoad &&
        prevProps.actionsDisabled === nextProps.actionsDisabled
    );
});